import { apiFetch } from "./apiFetch";
import type { AwsCreds } from "./awsCredentials";

export interface ScaleEcsServiceParams {
  clusterName: string;
  serviceName: string;
  region: string;
  desiredCount: 0 | 1;
  creds: AwsCreds;
}

export interface ScaleEcsServiceResult {
  serviceName: string;
  clusterName: string;
  status?: string;
  desiredCount: number;
  runningCount?: number;
  pendingCount?: number;
}

/**
 * Scale an ECS service to the given desired count (0 to stop, 1 to start) by
 * calling the API's `POST /inventory/live/ecs/:clusterName/:serviceName/scale`
 * endpoint with the caller's AWS credentials in the X-AWS-* headers.
 *
 * Resolves with the parsed service status, or throws an Error carrying the AWS
 * / API message so callers can surface it in a Flashbar.
 */
export async function scaleEcsService({
  clusterName,
  serviceName,
  region,
  desiredCount,
  creds,
}: ScaleEcsServiceParams): Promise<ScaleEcsServiceResult> {
  const res = await apiFetch(
    `/inventory/live/ecs/${encodeURIComponent(clusterName)}/${encodeURIComponent(serviceName)}/scale?region=${encodeURIComponent(region)}`,
    {
      method: "POST",
      headers: {
        "X-AWS-Access-Key-Id": creds.accessKeyId,
        "X-AWS-Secret-Access-Key": creds.secretAccessKey,
        "X-AWS-Session-Token": creds.sessionToken,
      },
      body: JSON.stringify({ desiredCount }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error?.message || `Scale failed: ${res.status}`);
  }

  return res.json();
}
